// Finding the brand inside a raw AI answer, so a visitor can see WHY a turn was judged
// "genoemd" instead of taking the verdict on faith.
//
// Deliberately fuzzy, because an exact substring match misses most real mentions: models
// inflect ("Wikipedia's", "Wikipedia-artikel"), drop or add spacing in multi-word brands
// ("VoorPositiviteit", "Voor-Positiviteit"), and vary case and accents. Matching only the
// literal name would highlight nothing in exactly the answers worth inspecting.
//
// It stays a plain string match rather than anything cleverer: this decorates a verdict
// the model already made, it does not re-decide it, so a missed highlight is cosmetic
// while a wrong one would be misleading.

export type Segment = { text: string; kind: 'plain' | 'brand' | 'excerpt' }

// Strips diacritics so "Café" matches "Cafe". Normalising to NFD splits an accented
// letter into base + combining mark, which the range then removes.
function foldAccents(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '')
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// buildBrandPattern turns a brand name into a regex tolerant of the variations models
// actually produce. Words may be separated by nothing, whitespace, hyphens or dots, and a
// match may carry a short suffix ("'s", "-artikel") without swallowing the next sentence.
function buildBrandPattern(brand: string): RegExp | null {
  const words = foldAccents(brand).trim().split(/\s+/).filter(Boolean).map(escapeRegex)
  if (words.length === 0) return null
  const joined = words.join('[\\s\\-.]{0,2}')
  // \p{L} keeps the suffix to letters, so "Wikipedia." does not eat the full stop.
  return new RegExp(`${joined}(?:['’]?s|\\p{L}{0,12})?`, 'giu')
}

// segment splits text into runs, marking where the brand appears and where the model's own
// supporting quote (the excerpt) sits. The excerpt wins on overlap: it is the model's
// stated evidence, so it is the more informative thing to show.
export function segment(text: string, brand: string, excerpt?: string): Segment[] {
  if (!text) return []

  type Span = { start: number; end: number; kind: 'brand' | 'excerpt' }
  const spans: Span[] = []

  // The excerpt is quoted verbatim from the answer, so a case-insensitive literal search
  // is enough and avoids any risk of a fuzzy rule mislabelling the evidence.
  if (excerpt && excerpt.trim()) {
    const hay = foldAccents(text).toLowerCase()
    const needle = foldAccents(excerpt.trim()).toLowerCase()
    const at = hay.indexOf(needle)
    if (at !== -1) spans.push({ start: at, end: at + needle.length, kind: 'excerpt' })
  }

  const pattern = brand.trim() ? buildBrandPattern(brand) : null
  if (pattern) {
    // Match against the accent-folded copy: it is the same length as the original, so
    // offsets map back one-to-one and the ORIGINAL text is what gets rendered.
    const folded = foldAccents(text)
    for (const m of folded.matchAll(pattern)) {
      if (m.index === undefined || m[0].length === 0) continue
      spans.push({ start: m.index, end: m.index + m[0].length, kind: 'brand' })
    }
  }

  if (spans.length === 0) return [{ text, kind: 'plain' }]

  // Excerpt first so it wins when two spans overlap.
  spans.sort((a, b) => a.start - b.start || (a.kind === 'excerpt' ? -1 : 1))

  const out: Segment[] = []
  let cursor = 0
  for (const s of spans) {
    if (s.start < cursor) continue // already covered by an earlier (winning) span
    if (s.start > cursor) out.push({ text: text.slice(cursor, s.start), kind: 'plain' })
    out.push({ text: text.slice(s.start, s.end), kind: s.kind })
    cursor = s.end
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), kind: 'plain' })
  return out
}
